import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';

import FastInternet from '~/src/components/BlockInternet/FastInternet';
import HomeInternet from '~/src/components/BlockInternet/HomeInternet';
import MiniBannersInternet from '~/src/components/BlockInternet/MiniBannersInternet';
import ChannelListPopup from '~/src/components/Bundles/ChannelListPopup';
import { ITariff } from '~/src/entities/formattedTariff';
import { selectArticles } from '~/src/features/internetV2/store/selectors';
import { selectIsAuth } from '~/src/store/selectors/auth/selectIsAuth';

import { AddOpportunities, IRepresentTopProps } from './page-blocks/AddOpportunities';
import { Annotation } from './page-blocks/Annotation';
import { HowItWork } from './page-blocks/HowItWork';
import { Questions } from './page-blocks/Questions';
import { dataQuestionsCountryHouse } from './page-blocks/Questions/data-questions-country-house/data';
import { dataQuestionsVillage } from './page-blocks/Questions/data-questions-village/data';
import { RepresentTop } from './page-blocks/RepresentTop';
import { Tariffs } from './page-blocks/Tariffs';

import * as ST from './styled';

export enum EPrivateHouse {
    Village = 'Village',
    CountryHouse = 'CountryHouse',
}

interface IProps {
    currentCity: ICurrentCity,
    addOpportunities: IRepresentTopProps['addOpportunities'] | null,
    agreement?: number,
    privateHouse?: EPrivateHouse,
}

export interface IQuestion {
    id: number,
    question: string,
    answer: string,
}

export const InternetRepresent = (
    {
        currentCity,
        addOpportunities,
        privateHouse,
    }: IProps,
): JSX.Element => {
    const [isOpenChannelsPopup, setIsOpenChannelsPopup] = useState(false);
    const [selectedTariff, setSelectedTariff] = useState<ITariff | null>(null);
    const articles = useSelector(selectArticles);

    const isAuth = useSelector(selectIsAuth);
    const onOpenPopupHandler = useCallback((tariff: ITariff) => {
        setIsOpenChannelsPopup(true);
        setSelectedTariff(tariff);
    }, []);

    const onClosePopupHandler = useCallback(() => {
        setIsOpenChannelsPopup(false);
    }, []);

    const isVillage = privateHouse === EPrivateHouse.Village;
    const isCountryHouse = privateHouse === EPrivateHouse.CountryHouse;

    const questions = useMemo(() => {
        if (isCountryHouse) {
            return dataQuestionsCountryHouse;
        } else if (isVillage) {
            return dataQuestionsVillage;
        } else {
           return articles;
        }
    }, [isCountryHouse, isVillage, articles]);

    return (
        <ST.InternetRepresent>
            <RepresentTop
                cityIn={currentCity.cityIn}
                privateHouse={privateHouse}
            />
            <ST.StyledGridContainer>
                <Annotation isCountryHouse={isCountryHouse}/>
            </ST.StyledGridContainer>
            <Tariffs
                privateHouse={privateHouse}
                openChannelsPopup={onOpenPopupHandler}
                isAuth={isAuth}
            />
            <ST.StyledGridContainer>
                <FastInternet privateHouse={privateHouse}/>
                <HomeInternet privateHouse={privateHouse}/>
                <MiniBannersInternet/>
                <HowItWork cityIn={currentCity.cityIn} isVillage={isVillage}/>
                {addOpportunities &&
                    <AddOpportunities
                        addOpportunities={addOpportunities}
                    />
                }
                <Questions
                    title={'Если остались вопросы — отвечаем'}
                    questions={questions}
                />

            </ST.StyledGridContainer>
            <ChannelListPopup
                isOpen={isOpenChannelsPopup}
                onClose={onClosePopupHandler}
                freePackets={selectedTariff?.freePackets}
            />
        </ST.InternetRepresent>
    );
};
